import { useEffect, useRef, useState, type ComponentType } from 'react';
import { Alert, Modal, Platform, Pressable, ScrollView, Text, View, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { C, alpha } from './theme';
import { Chip, Field } from './ui';
import { parseHora } from '../lib/time';
import { t } from '../lib/i18n';

const ALT = 44;   // altura de cada linha da rolagem
const VIS = 5;    // linhas visíveis (a do meio é a escolhida)
const p2 = (n: number) => String(n).padStart(2, '0');
const WEB = Platform.OS === 'web';

/** Uma coluna de rolagem (0..n-1) que encaixa na linha do meio. Funciona com dedo, mouse e roda do mouse. */
export function Roda({ n, valor, onChange, largura = 76 }: { n: number; valor: number; onChange: (v: number) => void; largura?: number }) {
  const ref = useRef<ScrollView>(null);
  const idx = useRef(valor);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [atual, setAtual] = useState(valor);
  const ir = (i: number, animated: boolean) => ref.current?.scrollTo({ x: 0, y: i * ALT, animated });

  // valor mudou por fora (ex.: depois de digitar): rola até ele
  useEffect(() => {
    if (valor !== idx.current) { idx.current = valor; setAtual(valor); ir(valor, false); }
  }, [valor]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function onScroll(e: NativeSyntheticEvent<NativeScrollEvent>) {
    const y = e.nativeEvent.contentOffset.y;
    const i = Math.max(0, Math.min(n - 1, Math.round(y / ALT)));
    setAtual(i);
    if (timer.current) clearTimeout(timer.current);
    // quando a rolagem para: encaixa (se ficou torto) e confirma o valor
    timer.current = setTimeout(() => {
      if (Math.abs(y - i * ALT) > 1) ir(i, true);
      if (i !== idx.current) { idx.current = i; onChange(i); }
    }, 140);
  }

  return (
    <ScrollView ref={ref} style={[{ height: ALT * VIS, width: largura }, WEB ? ({ scrollSnapType: 'y mandatory', overscrollBehavior: 'contain' } as any) : null]}
      contentContainerStyle={{ paddingVertical: ALT * Math.floor(VIS / 2) }}
      contentOffset={{ x: 0, y: valor * ALT }}
      onLayout={() => { ir(idx.current, false); if (WEB) setTimeout(() => ir(idx.current, false), 30); }}
      showsVerticalScrollIndicator={false} nestedScrollEnabled
      snapToInterval={ALT} decelerationRate="fast" scrollEventThrottle={16} onScroll={onScroll}>
      {Array.from({ length: n }, (_, i) => (
        <Pressable key={i} onPress={() => ir(i, true)}
          style={[{ height: ALT, alignItems: 'center', justifyContent: 'center' }, WEB ? ({ scrollSnapAlign: 'center' } as any) : null]}>
          <Text style={{ color: i === atual ? C.text : C.sub, fontSize: i === atual ? 24 : 18, fontWeight: i === atual ? '800' : '400',
            fontVariant: ['tabular-nums'], opacity: Math.abs(i - atual) > 1 ? 0.5 : 1 }}>{p2(i)}</Text>
        </Pressable>
      ))}
    </ScrollView>
  );
}

/** Duas colunas (horas 00-23 e minutos 00-59) com destaque na linha do meio. */
export function RodaHora({ h, m, onChange, cor }: { h: number; m: number; onChange: (h: number, m: number) => void; cor: string }) {
  return (
    <View style={{ height: ALT * VIS, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
      <View pointerEvents="none" style={{ position: 'absolute', left: 0, right: 0, top: ALT * Math.floor(VIS / 2), height: ALT,
        borderRadius: 12, backgroundColor: alpha(cor, 0.18), borderWidth: 1, borderColor: alpha(cor, 0.5) }} />
      <Roda n={24} valor={h} onChange={v => onChange(v, m)} />
      <Text style={{ color: C.text, fontSize: 24, fontWeight: '800', marginHorizontal: 6 }}>:</Text>
      <Roda n={60} valor={m} onChange={v => onChange(h, v)} />
    </View>
  );
}

/** Seletor nativo do aparelho ("Relógio"), fornecido por Pickers.tsx / Pickers.web.tsx. */
export type RelogioProps = { value: Date; cor: string; onChange: (d: Date) => void; onDone: (d: Date) => void; onCancel: () => void };

/**
 * Janela de escolha de horário: a rolagem é a opção principal; "Relógio" abre o seletor nativo
 * e "Digitar" mostra um campo HH:MM. Só aplica o valor no "OK".
 */
export function TimeModal({ value, onPick, onClose, cor, Relogio }:
  { value: Date; onPick: (d: Date) => void; onClose: () => void; cor: string; Relogio: ComponentType<RelogioProps> }) {
  const [h, setH] = useState(value.getHours());
  const [m, setM] = useState(value.getMinutes());
  const [modo, setModo] = useState<'roda' | 'relogio' | 'digitar'>('roda');
  const [txt, setTxt] = useState(`${p2(value.getHours())}:${p2(value.getMinutes())}`);
  const comHora = (hh: number, mm: number) => { const d = new Date(value); d.setHours(hh, mm, 0, 0); return d; };

  function ok() {
    if (modo === 'digitar') {
      const r = parseHora(txt);
      if (!r) return Alert.alert(t('Horário inválido'), t('Digite no formato HH:MM, por exemplo 13:30.'));
      onPick(comHora(r.h, r.m)); onClose(); return;
    }
    onPick(comHora(h, m)); onClose();
  }
  function trocar(novo: 'relogio' | 'digitar') {
    if (modo === 'digitar') { const r = parseHora(txt); if (r) { setH(r.h); setM(r.m); } }
    if (novo === 'digitar') setTxt(`${p2(h)}:${p2(m)}`);
    setModo(modo === novo ? 'roda' : novo);
  }

  return (
    <Modal transparent animationType="fade" onRequestClose={onClose}>
      <Pressable onPress={onClose} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', alignItems: 'center', padding: 16 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 20, padding: 18, gap: 14, width: '100%', maxWidth: 360 }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }}>{t('Escolher horário')}</Text>
          {modo === 'roda' && <RodaHora h={h} m={m} cor={cor} onChange={(a, b) => { setH(a); setM(b); }} />}
          {modo === 'relogio' && (
            <Relogio value={comHora(h, m)} cor={cor} onChange={d => { setH(d.getHours()); setM(d.getMinutes()); }}
              onDone={d => { onPick(comHora(d.getHours(), d.getMinutes())); onClose(); }} onCancel={() => setModo('roda')} />
          )}
          {modo === 'digitar' && (
            <View style={{ gap: 6 }}>
              <Field value={txt} onChangeText={setTxt} autoFocus placeholder="HH:MM" maxLength={5} selectTextOnFocus
                keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : Platform.OS === 'android' ? 'phone-pad' : 'default'}
                onSubmitEditing={ok} style={{ fontSize: 30, textAlign: 'center', fontWeight: '700', fontVariant: ['tabular-nums'] }} />
              <Text style={{ color: C.sub, fontSize: 12, textAlign: 'center' }}>{t('Exemplos: 13:30, 1330, 9:05')}</Text>
            </View>
          )}
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8 }}>
            <Chip small cor={cor} on={modo === 'relogio'} label={'🕐 ' + t('Relógio')} onPress={() => trocar('relogio')} />
            <Chip small cor={cor} on={modo === 'digitar'} label={'⌨️ ' + t('Digitar')} onPress={() => trocar('digitar')} />
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
            <Pressable onPress={onClose} hitSlop={8}><Text style={{ color: C.sub, fontSize: 16 }}>{t('Cancelar')}</Text></Pressable>
            <Pressable onPress={ok} hitSlop={8} style={{ marginLeft: 14 }}><Text style={{ color: cor, fontSize: 16, fontWeight: '700' }}>{t('OK')}</Text></Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
